import NotificationModelWrapper from "./NotificationModelWrapper";
import * as Yup from "yup";
import { Box, Button, MenuItem, Stack, TextField } from "@mui/material";
import {
  ArticleDropDown,
  Form,
  FormImageInput,
  FormInput,
  FormVideoInput,
  SubmitLoadingButton,
} from "../../../../../components/Forms";
import { Formik } from "formik";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { createNotification } from "../../../../../services/notificationService";
import { getAllActiveArticle } from "../../../../../services/articleService";
import { ErrorMessage, Loader } from "../../../../../components";
import * as toast from "../../../../../Toast";
import RadioButtonGroup from "../../../../../components/Forms/RadioButtonGroup";

const options = ["Article", "Insight"];

function ArticleNotificationFormModal({
  showModal,
  toggleModal,
  title,
  subtitle,
}) {
  // insight, acticle
  const [eventType, setEventType] = useState("");

  if (!showModal) return null;

  return (
    <NotificationModelWrapper
      toggleModal={toggleModal}
      title={title}
      subtitle={subtitle}
    >
      <TextField
        select
        fullWidth
        id="eventType"
        size="small"
        label="Notification Type"
        onChange={(e) => {
          setEventType(e.target.value);
        }}
        sx={{
          mb: 1,
          width: "100%",
        }}
        value={eventType}
        SelectProps={{
          MenuProps: {
            PaperProps: {
              sx: { maxHeight: 320 },
            },
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" },
          },
        }}
      >
        {options.map((option, index) => (
          <MenuItem value={option} key={index}>
            {option}
          </MenuItem>
        ))}
      </TextField>

      {eventType === "Article" && (
        <ArticleNotificationFormWrapper
          toggleModal={toggleModal}
          eventType={eventType}
        />
      )}

      {eventType === "Insight" && (
        <InsightNotificationForm
          toggleModal={toggleModal}
          eventType={eventType}
        />
      )}
    </NotificationModelWrapper>
  );
}

// login validationSchema
const articleNotificationFormSchema = Yup.object().shape({
  // notificationTitle: Yup.string()
  //   .required()
  //   .min(2)
  //   .max(200, "Notification Title must be less than 200 characters")
  //   .label("Notification Text")
  //   .trim(),
  notificationText: Yup.string()
    .required()
    .min(2)
    .max(200, "Notification Text must be less than 200 characters")
    .label("Notification Text")
    .trim(),
  referenceId: Yup.string().required().min(2).label("Article").trim(),
});

function ArticleNotificationFormWrapper({ eventType, toggleModal }) {
  const { isError, isLoading, data, error } = useQuery(
    ["articles"],
    () => getAllActiveArticle(),
    { refetchOnWindowFocus: true },
  );

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <ErrorMessage message={error.response.data.message} />;
  }

  return (
    <ArticleNotificationForm
      toggleModal={toggleModal}
      articleList={data.data.data["articles"]}
      eventType={eventType}
    />
  );
}

function ArticleNotificationForm({ eventType, toggleModal, articleList }) {
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: (data) => createNotification(data),
  });
  const queryClient = useQueryClient();

  const handleSubmit = async (inputs) => {
    try {
      const notificationTitle = articleList.find(
        (article) => article.id === inputs.referenceId,
      ).articleTitle;

      await mutateAsync({
        eventType: eventType.toLowerCase(),
        notificationTitle,
        date: new Date().toISOString(),
        ...inputs,
      });
      toggleModal();
      toast.success("Successfully Added Notification");
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    } catch (error) {
      toast.error("Notification could not be created");
    }
  };

  return (
    <Form
      initialValues={{
        notificationText: "",
        // notificationTitle: "",
        referenceId: "",
      }}
      onSubmit={handleSubmit}
      validationSchema={articleNotificationFormSchema}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <ArticleDropDown
          label="Choose Article"
          name="referenceId"
          articleList={articleList}
        />

        {/* <FormInput
          label="Notification Title"
          name="notificationTitle"
          type="text"
        /> */}

        <FormInput
          label="Notification Text"
          name="notificationText"
          type="text"
        />

        <Stack spacing={2} direction="row">
          <Button fullWidth variant="outlined" onClick={toggleModal}>
            CANCEL
          </Button>
          <SubmitLoadingButton
            buttonText="Send Notification"
            type="submit"
            submitButton={true}
            loading={isLoading}
          />
        </Stack>
        {/* <SuccessAndErrorMessage error={state.error} success={state.message} /> */}
      </Box>
    </Form>
  );
}

const maxFileSize = {
  image: 1 * 1024 * 1024, // 1 MB in bytes
  video: 20 * 1024 * 1024, // 20 MB in bytes
  thumbnail: 500 * 1024, // 500 KB
};

// insight validationSchema
const insightNotificationFormSchema = Yup.object().shape({
  notificationTitle: Yup.string()
    .required()
    .min(2)
    .max(50, "Notification Title must be less than 50 characters")
    .label("Notification Text")
    .trim(),
  notificationText: Yup.string()
    .required()
    .min(2)
    .max(70, "Notification Text must be less than 70 characters")
    .label("Notification Text")
    .trim(),

  insightType: Yup.string()
    .required()
    .oneOf(["image", "video", "none"])
    .label("Insight Type")
    .trim(),
  selectedThumbnail: Yup.mixed().when("insightType", {
    is: (insightType) => insightType !== "none",
    then: Yup.mixed()
      .required("Thumbnail image is required")
      .test(
        "type",
        "Thumbnail must be .jpeg, .jpg, .png, or .webp",
        (value) =>
          !value ||
          (value &&
            (value.type === "image/jpeg" ||
              value.type === "image/png" ||
              value.type === "image/webp" ||
              value.type === "image/jpg")),
      )
      .test("size", "Thumbnail size must not exceed 500KB", (value) => {
        return !value || (value && value.size <= maxFileSize.thumbnail);
      })
      .label("Thumbnail"),
    otherwise: Yup.mixed().nullable(),
  }),
  checkResolution: Yup.boolean().required().oneOf([false]),

  selectedImage: Yup.mixed().when("insightType", {
    is: "image", // When insightType is 'image'
    then: Yup.mixed()
      .required("Image is required")
      .test(
        "type",
        "Only the following formats are accepted: .jpeg, .jpg, .png, and .webp",
        (value) => {
          return (
            value &&
            (value.type === "image/jpeg" ||
              value.type === "image/png" ||
              value.type === "image/webp" ||
              value.type === "image/jpg")
          );
        },
      )
      .test("size", "File size too large", (value) => {
        return value && value.size <= maxFileSize.image;
      })
      .label("Image"),
    otherwise: Yup.mixed().nullable(), // If not 'image', this field is not required
  }),
  selectedVideo: Yup.mixed().when("insightType", {
    is: "video", // When insightType is 'video'
    then: Yup.mixed()
      .required("Video is required")
      .test(
        "type",
        "Only the following formats are accepted: .mp4, .mov, .avi, and .webm",
        (value) => {
          return (
            value &&
            (value.type === "video/mp4" ||
              value.type === "video/mov" ||
              value.type === "video/quicktime" ||
              value.type === "video/avi" ||
              value.type === "video/webm")
          );
        },
      )
      .test("size", "File size too large", (value) => {
        return value && value.size <= maxFileSize.video;
      })
      .label("Video"),
    otherwise: Yup.mixed().nullable(), // If not 'video', this field is not required
  }),
});

const insightTypeOptions = [
  { key: "Image", value: "image" },
  { key: "Video", value: "video" },
  { key: "None", value: "none" },
];

function InsightNotificationForm({ eventType, toggleModal, articleList }) {
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: (data) => createNotification(data),
  });
  const queryClient = useQueryClient();

  const handleSubmit = async (inputs) => {
    try {
      const formData = new FormData();
      formData.append("eventType", eventType.toLowerCase());
      formData.append("notificationTitle", inputs.notificationTitle);
      formData.append("notificationText", inputs.notificationText);
      formData.append("insightType", inputs.insightType);
      formData.append("date", new Date().toISOString());

      if (inputs.insightType === "image" && inputs.selectedImage) {
        formData.append("file", inputs.selectedImage);
      }

      if (inputs.insightType === "video" && inputs.selectedVideo) {
        formData.append("file", inputs.selectedVideo);
      }

      if (inputs.insightType !== "none" && inputs.selectedThumbnail) {
        formData.append("thumbnail", inputs.selectedThumbnail);
      }

      await mutateAsync(formData);
      toggleModal();
      toast.success("Successfully Added Notification");
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    } catch (error) {
      toast.error("Notification could not be created");
    }
  };

  const initialValues = {
    notificationText: "",
    notificationTitle: "",
    insightType: "image",
    selectedImage: "",
    selectedVideo: "",
    selectedThumbnail: "",
    checkResolution: false,
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={insightNotificationFormSchema}
    >
      {({ values, setFieldValue, setFieldTouched, setFieldError }) => {
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <FormInput
              label="Notification Title"
              name="notificationTitle"
              type="text"
            />

            <FormInput
              label="Notification Text"
              name="notificationText"
              type="text"
            />

            <RadioButtonGroup
              name="insightType"
              options={insightTypeOptions}
              onChange={(value) => {
                setFieldValue("selectedImage", "");
                setFieldValue("selectedVideo", "");
                setFieldValue("selectedThumbnail", "");
                setFieldValue("checkResolution", false);
              }}
              layout="row"
            />

            {values.insightType === "image" ? (
              <FormImageInput
                name="selectedImage"
                imageValues={values.articleImage}
                onChangeSelectedImage={(event) => {
                  setFieldTouched(event.target.name);
                  setFieldValue(event.target.name, event.target.files[0]);
                }}
                onError={(message) => {
                  setFieldError("selectedImage", message);
                }}
                onCheckRes={(checkResolution) => {
                  setFieldValue("checkResolution", checkResolution);
                }}
                checkResolution={values.checkResolution}
              />
            ) : null}
            {values.insightType === "video" ? (
              <FormVideoInput
                name="selectedVideo"
                videoValues={values.articleImage}
                onChangeSelectedVideo={(event) => {
                  setFieldTouched(event.target.name);
                  setFieldValue(event.target.name, event.target.files[0]);
                }}
                onError={(message) => {
                  setFieldError("selectedVideo", message);
                }}
                onCheckRes={(checkResolution) => {
                  setFieldValue("checkResolution", checkResolution);
                }}
                checkResolution={values.checkResolution}
              />
            ) : null}

            {values.insightType !== "none" ? (
              <>
                <FormImageInput
                  name="selectedThumbnail"
                  label="Upload Thumbnail Image"
                  imageValues={values.thumbnailImage}
                  onChangeSelectedImage={(event) => {
                    setFieldTouched(event.target.name);
                    setFieldValue(event.target.name, event.target.files[0]);
                  }}
                  onError={(message) => {
                    setFieldError("selectedThumbnail", message);
                  }}
                  onCheckRes={() => setFieldValue("checkResolution", false)}
                  checkResolution={false}
                />
                <p className="note" style={{ marginTop: -8, marginBottom: 0 }}>
                  Thumbnail image (max 500KB)
                </p>
              </>
            ) : null}

            <Stack spacing={2} direction="row">
              <Button fullWidth variant="outlined" onClick={toggleModal}>
                CANCEL
              </Button>
              <SubmitLoadingButton
                buttonText="Send Notification"
                type="submit"
                submitButton={true}
                loading={isLoading}
              />
            </Stack>
            {/* <SuccessAndErrorMessage error={state.error} success={state.message} /> */}
          </Box>
        );
      }}
    </Formik>
  );
}

export default ArticleNotificationFormModal;
